import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePermissions } from '@/features/auth';
import { buildDealerNameMap, useDealersByIds } from '@features/dealers/hooks/useDealersByIds';
import type { HistoryPage } from '../types/agreementTimeline.types';
import { fetchAgreementHistory, HISTORY_PER_PAGE } from '../services/agreementHistory';
import { agreementKeys } from './agreementKeys';

const takingDealerIds = (page: HistoryPage | undefined): string[] =>
    (page?.entries ?? []).flatMap((entry) =>
        entry.kind === 'top_up_added' && entry.topUp.takingDealerId ? [entry.topUp.takingDealerId] : [],
    );

const withDealerNames = (page: HistoryPage | undefined, names: Map<string, string>): HistoryPage | undefined =>
    page && names.size > 0
        ? {
              ...page,
              entries: page.entries.map((entry) =>
                  entry.kind === 'top_up_added' && entry.topUp.takingDealerId
                      ? { ...entry, topUp: { ...entry.topUp, takingDealerName: names.get(entry.topUp.takingDealerId) ?? null } }
                      : entry,
              ),
          }
        : page;

/** Fetched eagerly rather than on tab activation, so the tab opens without a spinner.
 *  It is its own query, so a slow or failing fetch never blanks the detail page. */
export function useAgreementHistory(id: string | undefined) {
    const [pageState, setPageState] = useState({ id, page: 1 });
    const requestedPage = pageState.id === id ? pageState.page : 1;
    const setPage = useCallback((next: number) => setPageState({ id, page: next }), [id]);

    const query = useQuery({
        queryKey: agreementKeys.historyPage(id ?? '', requestedPage, HISTORY_PER_PAGE),
        queryFn: () => fetchAgreementHistory(id!, { page: requestedPage, perPage: HISTORY_PER_PAGE }),
        enabled: !!id,
        placeholderData: (previousData, previousQuery) =>
            previousQuery?.queryKey[2] === (id ?? '') ? previousData : undefined,
    });

    /** Clamps the page STATE, not just the reported value, so the query refetches
     *  at the clamped page rather than showing an out-of-range page's empty body. */
    const lastPage = query.data?.meta.lastPage;

    useEffect(() => {
        if (lastPage !== undefined && requestedPage > lastPage) {
            setPageState({ id, page: lastPage });
        }
    }, [id, lastPage, requestedPage]);

    const { hasPermission } = usePermissions();
    const canBrowseDealers = hasPermission({ resource: 'dealer', action: 'browse' });
    const dealerIds = useMemo(() => takingDealerIds(query.data), [query.data]);
    const dealers = useDealersByIds(dealerIds, canBrowseDealers);
    const history = useMemo(() => withDealerNames(query.data, buildDealerNameMap(dealers.data)), [query.data, dealers.data]);

    return { query, history, page: requestedPage, setPage };
}
